import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { MessageSquare, Package, FileText, TrendingUp, Bell, Search, Settings, LogOut, Menu, X, Calendar, Users, ShoppingCart, Calculator, Truck, ClipboardList, Plus, Donut, BowArrow } from 'lucide-react';
import Swal from 'sweetalert2';
import Footer from './Footer';

const PharmacyLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Fonction pour obtenir la salutation selon l'heure
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon après-midi';
    return 'Bonsoir';
  };

  // Fonction pour obtenir la date formatée
  const getFormattedDate = () => {
    return new Date().toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const menuItems = [
    // V1 — actifs
    { icon: TrendingUp, label: 'Tableau de bord',  path: '/pharmacist',              available: true },
    { icon: Calendar,   label: 'Horaires & Garde', path: '/pharmacist/garde',        available: true },
    { icon: Users,      label: 'Mes Employés',     path: '/pharmacist/vendor',       available: true },
    { icon: Settings,   label: 'Mon Syndicat',     path: '/pharmacist/syndicat',     available: true },
    { icon: Package,    label: 'Ma Pharmacie',     path: '/pharmacist/ma-pharmacie', available: true },

    // V2+ — commentés (décommenter quand prêt)
    // { icon: MessageSquare, label: 'Messages Patients',  path: '/pharmacist/messages',           available: true },
    // { icon: MessageSquare, label: 'Messagerie',         path: '/pharmacist/messagespharmacist', available: true },
    // { icon: Package,       label: 'Gestion du stock',  path: '/pharmacist/stock',              available: true },
    // { icon: Calculator,    label: 'Calculatrice',       path: '/pharmacist/calcule',            available: true },
    // { icon: Truck,         label: 'Laboratoire',        path: '/pharmacist/lab',                available: true },
    // { icon: ShoppingCart,  label: 'Point de Vente',     path: '/pharmacist/sales',              available: true },
    // { icon: Donut,         label: 'Mutuelles',          path: '/pharmacist/mutuelle',           available: true },
    // { icon: BowArrow,      label: 'Dashboard',          path: '/pharmacist/dash',               available: true },
    // { icon: BowArrow,      label: 'Dashboard employé',  path: '/pharmacist/dashEmp',            available: true },
    // { icon: Truck,         label: 'Livraison',          path: '/pharmacist/livraison',          available: true },
  ];

  const handleMenuClick = (item) => {
    if (item.available) {
      navigate(item.path);
    } else {
      Swal.fire({
        icon: 'info',
        title: 'Oups...',
        text: "Cette fonctionnalité n'est pas encore disponible !",
        confirmButtonText: 'OK',
        confirmButtonColor: '#16A34A',
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userName');
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/pharmacist') {
      return location.pathname === '/pharmacist';
    }
    return location.pathname.endsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full bg-white shadow-sm transition-all duration-300 z-50 border-r border-gray-200 ${sidebarOpen ? 'w-64' : 'w-0'} overflow-hidden`}>
        <div className="flex flex-col h-full">
          {/* Logo & Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8">
                    <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 8V16M8 12H16" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                </div>
                
                <div>
                  <h2 className="text-xl font-bold text-gray-900">SunuFarmasi</h2>
                  <p className="text-xs text-green-600 font-medium">Pharmacien</p>
                </div>
              </div>
              <button 
                onClick={() => setSidebarOpen(false)} 
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-all"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={() => handleMenuClick(item)}
                disabled={!item.available}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all font-medium text-sm ${
                  isActive(item.path)
                    ? 'bg-green-50 text-green-600'
                    : item.available
                    ? 'text-gray-700 hover:bg-gray-50'
                    : 'text-gray-400 cursor-not-allowed opacity-50'
                }`}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
                {!item.available && (
                  <span className="ml-auto text-xs bg-gray-200 px-2 py-0.5 rounded-full">Bientôt</span>
                )}
              </button>
            ))}
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-gray-200">
            <button 
              onClick={handleLogout} 
              className="w-full flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-all font-medium text-sm"
            >
              <LogOut size={20} />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-0'}`}>
        <header className="bg-white sticky top-0 z-40 border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)} 
                className="p-2.5 hover:bg-gray-100 rounded-lg transition-all"
              >
                <Menu size={24} className="text-gray-700" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {getGreeting()}, Dr. Touré
                </h1>
                <p className="text-sm text-gray-600">
                  {getFormattedDate()}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="relative hidden md:block">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  className="pl-11 pr-4 py-2.5 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                />
              </div>

              <button className="relative p-2.5 hover:bg-gray-100 rounded-lg transition-all">
                <Bell size={22} className="text-gray-600" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>

              <div className="flex items-center space-x-3 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-9 h-9 bg-green-600 rounded-lg flex items-center justify-center text-white font-bold">
                  DT
                </div>
                <div className="hidden lg:block">
                  <p className="text-sm font-semibold text-gray-900">Dr. Touré</p>
                  <p className="text-xs text-gray-600">Pharmacie Mor Mbacké</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6No">
          <Outlet />
        </main>

        <Footer />
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default PharmacyLayout;